import React, { useRef, useState } from "react";
import { Autocomplete } from "@mui/material";

// フォーカス時に入力内容を全選択 (ID + name 表示を防ぐため)
const handleFocusSelectAll = (e) => {
  setTimeout(() => e.target.select?.(), 0);
};

// 検索ボックスのソートアルゴリズム。
// 並び替えキー: [score, position, label.length] の昇順。
//   score: 0=id 完全一致 / 1=label 完全一致 / 2=label 前方一致 / 3=id 前方一致 /
//          4=label 含む / 5=id 含む / null=不一致 (除外)
//   コード入力を優先するため、id 完全一致を最優先 (score 0) にする。
//   例: 階級コード「5」を入力したとき、名称が「5」のコード「50」より、
//       コード「5」の候補を先頭に出す (名称検索と挙動を揃える)。
//   position: score 4-5 では term の出現位置 (indexOf)。score 0-3 では 0 固定。
//   label.length: 同 score・同 position 内のタイブレーク (短い順)。
const scoreOption = (option, term, labelKey, idKeys) => {
  const label = (option?.[labelKey] ?? "").toString().toLowerCase();
  const ids = (idKeys || []).map((k) =>
    (option?.[k] ?? "").toString().toLowerCase(),
  );

  if (ids.some((id) => id === term)) return [0, 0, label.length];
  if (label === term) return [1, 0, label.length];
  if (label.startsWith(term)) return [2, 0, label.length];
  if (ids.some((id) => id.startsWith(term))) return [3, 0, label.length];

  const labelPos = label.indexOf(term);
  if (labelPos > 0) return [4, labelPos, label.length];

  let minIdPos = Infinity;
  for (const id of ids) {
    const pos = id.indexOf(term);
    if (pos >= 0 && pos < minIdPos) minIdPos = pos;
  }
  if (minIdPos !== Infinity) return [5, minIdPos, label.length];

  return null;
};

const defaultFilterAndSort = (options, inputValue, labelKey, idKeys) => {
  if (!inputValue) return options;
  const term = inputValue.toLowerCase();
  const scored = [];
  for (const option of options) {
    const key = scoreOption(option, term, labelKey, idKeys);
    if (key !== null) scored.push({ option, key });
  }
  scored.sort((a, b) => {
    for (let i = 0; i < 3; i++) {
      if (a.key[i] !== b.key[i]) return a.key[i] - b.key[i];
    }
    return 0;
  });
  return scored.map((s) => s.option);
};

/**
 * MUI Autocomplete ラッパー — Enter でキーボードでハイライトした候補を選択
 * (マウスホバーによるハイライト変更は無視)。未操作時は絞り込み結果の先頭にフォールバック。
 * + ArrowLeft/Right でフィールド間移動、onSelectMoveNext で選択後に次へ移動
 * + 非 freeSolo の場合、blur 時にマスタに存在しない入力は値をクリアしてエラー表示する。
 *   ID をバックエンドへ送る必要があるため、無効な文字列を保持しない。
 */
const SmartAutocomplete = ({
  inputRef,
  onSelectMoveNext,
  onArrowNav, // (direction: -1 | 1) => void
  filterOptions,
  // 検索ボックスのデフォルト filter+sort 用のキー指定。
  // filterOptions を渡さない場合のみ有効。両方指定すると label と id のいずれかが
  // term と完全一致 / 前方一致 / 含む の優先順で並び替えされる。
  labelKey,
  idKeys,
  options,
  onChange,
  onInputChange,
  renderInput,
  freeSolo,
  value,
  getOptionLabel,
  errorMessage = "存在しない値です",
  ...rest
}) => {
  const filteredRef = useRef(options);
  const inputValueRef = useRef("");
  const [highlightedOption, setHighlightedOption] = useState(null);
  const [invalid, setInvalid] = useState(false);

  const internalFilterOptions = (opts, state) => {
    let filtered;
    if (filterOptions) {
      // 呼び出し側が独自の filterOptions を渡している場合はそれを尊重する
      filtered = filterOptions(opts, state);
    } else if (labelKey && idKeys) {
      // labelKey + idKeys が指定されていればデフォルトのスマート filter+sort を適用
      filtered = defaultFilterAndSort(opts, state.inputValue, labelKey, idKeys);
    } else {
      filtered = opts;
    }
    filteredRef.current = filtered;
    return filtered;
  };

  const handleChange = (e, v, reason) => {
    onChange?.(e, v, reason);
    setInvalid(false);
    if (v && onSelectMoveNext) onSelectMoveNext();
  };

  const handleInputChange = (e, v, reason) => {
    inputValueRef.current = v;
    if (invalid) setInvalid(false);
    // 入力が空になったらキーボードハイライトもリセット。
    // 商品選択後に入力をクリアして Enter を押したとき、前回のハイライトが
    // 誤って選ばれて (=元の商品が再選択されて) しまうのを防ぐ。
    if (!v) setHighlightedOption(null);
    onInputChange?.(e, v, reason);
  };

  const handleBlur = () => {
    setHighlightedOption(null);
    if (freeSolo) return;

    const currentInput = (inputValueRef.current ?? "").trim();

    // 空入力は許容 (値も null にする)
    if (!currentInput) {
      if (value != null) onChange?.(null, null, "clear");
      setInvalid(false);
      return;
    }

    // 既に選択中の値とラベルが一致していれば OK
    const labelOfValue = value && getOptionLabel ? getOptionLabel(value) : "";
    if (currentInput === labelOfValue) {
      setInvalid(false);
      return;
    }

    // 入力文字列がいずれかの選択肢のラベルと完全一致するならその値を採用
    const match = (options || []).find(
      (opt) => getOptionLabel?.(opt) === currentInput,
    );
    if (match) {
      onChange?.(null, match, "selectOption");
      setInvalid(false);
    } else {
      // マスタに存在しない → 値をクリアしてエラー表示
      onChange?.(null, null, "clear");
      setInvalid(true);
    }
  };

  const handleKeyDown = (e) => {
    const input = e.target;
    if (e.key === "Enter" && !e.shiftKey) {
      // クリア状態 (入力が空) + 矢印キーでハイライトしていない → 先頭候補を自動選択せず移動のみ。
      // ハイライト済みの場合は通常通り選択させる。
      if (!input.value?.trim() && !highlightedOption) {
        e.preventDefault();
        if (onSelectMoveNext) onSelectMoveNext();
        return;
      }
      // キーボードでハイライトした候補を優先。未操作なら絞り込み結果の先頭。
      const target = highlightedOption || filteredRef.current?.[0];
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        handleChange(null, target, "selectOption");
      } else if (freeSolo && input.value) {
        // freeSolo の場合、候補がなくても入力値をそのまま使って次へ
        e.preventDefault();
        handleChange(null, input.value, "createOption");
      } else if (onSelectMoveNext) {
        e.preventDefault();
        onSelectMoveNext();
      }
      return;
    }
    if (onArrowNav) {
      if (e.key === "ArrowLeft" && input.selectionStart === 0) {
        e.preventDefault();
        onArrowNav(-1);
      } else if (
        e.key === "ArrowRight" &&
        input.selectionEnd === input.value.length
      ) {
        e.preventDefault();
        onArrowNav(1);
      }
    }
  };

  const wrappedRenderInput = (params) => {
    // MUI Autocomplete は params.inputProps.onKeyDown に独自の Enter ハンドラを仕込んでおり、
    // TextField レベルの onKeyDown よりも先に走る。クリア状態 + autoHighlight で
    // 先頭候補が選ばれてしまうのを防ぐため、input 要素の onKeyDown を直接ラップして抑止する。
    // 矢印キーでハイライトしている場合 (highlightedOption !== null) は通常通り選択させる。
    const muiInputOnKeyDown = params.inputProps?.onKeyDown;
    const wrappedInputProps = {
      ...params.inputProps,
      onKeyDown: (e) => {
        if (
          e.key === "Enter" &&
          !e.shiftKey &&
          !e.currentTarget.value?.trim() &&
          !highlightedOption
        ) {
          e.preventDefault();
          e.stopPropagation();
          if (onSelectMoveNext) onSelectMoveNext();
          return;
        }
        muiInputOnKeyDown?.(e);
      },
    };
    const base = renderInput({ ...params, inputProps: wrappedInputProps });
    return React.cloneElement(base, {
      inputRef,
      onKeyDown: handleKeyDown,
      onFocus: (e) => {
        base.props.onFocus?.(e);
        handleFocusSelectAll(e);
      },
      error: invalid || !!base.props.error,
      helperText: invalid ? errorMessage : base.props.helperText,
    });
  };

  return (
    <Autocomplete
      {...rest}
      value={value}
      freeSolo={freeSolo}
      options={options}
      getOptionLabel={getOptionLabel}
      filterOptions={internalFilterOptions}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onHighlightChange={(_event, option, reason) => {
        // キーボード操作 (ArrowUp/Down) のみ反映する。
        // autoHighlight (reason === "auto") やマウス/タッチによる変更は無視。
        // → クリア状態で Enter を押したときに、autoHighlight された先頭候補が
        //   意図せず選ばれてしまうのを防ぐため。
        if (reason !== "keyboard") return;
        setHighlightedOption(option);
      }}
      onBlur={handleBlur}
      renderInput={wrappedRenderInput}
      autoHighlight
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
    />
  );
};

export default SmartAutocomplete;
